// Normalizes an upload validation failure payload without assuming a fixed
// backend shape and without dropping any field the backend sent — every
// key on an issue becomes a visible {label, value} pair unless it's one of
// the recognized title/message/severity/location/fix fields.

const TITLE_KEYS = ['title', 'name', 'error'];
const MESSAGE_KEYS = ['message', 'description', 'detail'];
const FIX_KEYS = ['recommendation', 'fix', 'how_to_fix', 'howToFix', 'suggestion'];
const EXAMPLE_KEYS = ['example', 'example_fix', 'exampleFix', 'sample', 'sample_fix'];
const SEVERITY_KEYS = ['severity', 'level'];
const LOCATION_KEYS = ['worksheet', 'sheet', 'column', 'field', 'row', 'rows', 'row_range', 'rowRange'];
const SKIP_KEYS = new Set([...TITLE_KEYS, ...MESSAGE_KEYS, ...FIX_KEYS, ...EXAMPLE_KEYS, ...SEVERITY_KEYS, ...LOCATION_KEYS]);

function humanizeLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function stringifyValue(v) {
  return Array.isArray(v) ? v.join(', ') : String(v);
}

function normalizeIssue(item, defaultSeverity) {
  if (typeof item === 'string') {
    return { severity: defaultSeverity, title: item, message: '', location: [], fields: [], fix: null, example: null };
  }
  if (!item || typeof item !== 'object') return null;

  const title = TITLE_KEYS.map(k => item[k]).find(v => v !== undefined) ?? '';
  const message = MESSAGE_KEYS.map(k => item[k]).find(v => v !== undefined) ?? (title ? '' : '');
  const fix = FIX_KEYS.map(k => item[k]).find(v => v !== undefined) ?? null;
  const example = EXAMPLE_KEYS.map(k => item[k]).find(v => v !== undefined) ?? null;
  const severity = SEVERITY_KEYS.map(k => item[k]).find(v => v !== undefined) ?? defaultSeverity;

  const location = LOCATION_KEYS
    .filter(key => item[key] !== undefined && item[key] !== null)
    .map(key => ({ label: humanizeLabel(key), value: stringifyValue(item[key]) }));

  const fields = Object.keys(item)
    .filter(key => !SKIP_KEYS.has(key) && item[key] !== undefined && item[key] !== null)
    .map(key => ({ label: humanizeLabel(key), value: stringifyValue(item[key]) }));

  return {
    severity,
    title: title || message || 'Issue',
    message: title && message ? message : '',
    location,
    fields,
    fix,
    example,
  };
}

function extractList(payload, keys) {
  for (const key of keys) {
    const parts = key.split('.');
    let value = payload;
    for (const part of parts) {
      value = value?.[part];
    }
    if (Array.isArray(value) && value.length) return value;
  }
  return [];
}

export function normalizeValidationIssues(payload) {
  if (!payload || typeof payload !== 'object') return { errors: [], warnings: [] };

  const rawErrors = extractList(payload, ['errors', 'validation_errors', 'detail.errors', 'validation.errors']);
  const rawWarnings = extractList(payload, ['warnings', 'detail.warnings', 'validation.warnings']);

  return {
    errors: rawErrors.map(i => normalizeIssue(i, 'error')).filter(Boolean),
    warnings: rawWarnings.map(i => normalizeIssue(i, 'warning')).filter(Boolean),
  };
}

export function hasValidationIssues(payload) {
  if (!payload || typeof payload !== 'object') return false;
  if (extractList(payload, ['errors', 'validation_errors', 'detail.errors', 'validation.errors']).length) return true;
  if (payload.valid === false) return true;
  const status = String(payload.status || '').toLowerCase();
  return status === 'error' || status === 'invalid';
}
